import React, { createContext, useContext, useCallback, useMemo, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { consumeSettingsOrigin, rememberSettingsOrigin } from '@/lib/settings-navigation';

// Only account-level sections (no business settings)
export type SettingsSection = 'profile' | 'plan' | 'security';

const VALID_SECTIONS: SettingsSection[] = ['profile', 'plan', 'security'];

function parseSection(search: string): SettingsSection {
  const params = new URLSearchParams(search);
  const raw = params.get('section');
  if (raw && VALID_SECTIONS.includes(raw as SettingsSection)) {
    return raw as SettingsSection;
  }
  return 'profile';
}

interface AccountSettingsContextType {
  isAccountSettingsOpen: boolean;
  activeSection: SettingsSection;
  toggleAccountSettings: () => void;
  openAccountSettings: (section?: SettingsSection) => void;
  closeAccountSettings: () => void;
  setActiveSection: (section: SettingsSection) => void;
}

const AccountSettingsContext = createContext<AccountSettingsContextType | undefined>(undefined);

export const useAccountSettings = () => {
  const context = useContext(AccountSettingsContext);
  if (context === undefined) {
    throw new Error('useAccountSettings must be used within an AccountSettingsProvider');
  }
  return context;
};

interface AccountSettingsProviderProps {
  children: ReactNode;
}

export const AccountSettingsProvider: React.FC<AccountSettingsProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isAccountSettingsOpen = location.pathname.startsWith('/settings/account');
  const activeSection = isAccountSettingsOpen ? parseSection(location.search) : 'profile';

  const openAccountSettings = useCallback((section?: SettingsSection) => {
    rememberSettingsOrigin(location.pathname, location.search);
    const sec = section || 'profile';
    navigate(`/settings/account?section=${sec}`);
  }, [navigate, location.pathname, location.search]);

  const closeAccountSettings = useCallback(() => {
    if (!location.pathname.startsWith('/settings/account')) return;
    navigate(consumeSettingsOrigin());
  }, [navigate, location.pathname]);

  const toggleAccountSettings = useCallback(() => {
    if (isAccountSettingsOpen) {
      closeAccountSettings();
    } else {
      openAccountSettings();
    }
  }, [isAccountSettingsOpen, closeAccountSettings, openAccountSettings]);

  const setActiveSection = useCallback((section: SettingsSection) => {
    navigate(`/settings/account?section=${section}`, { replace: true });
  }, [navigate]);

  const contextValue = useMemo(() => ({
    isAccountSettingsOpen,
    activeSection,
    toggleAccountSettings,
    openAccountSettings,
    closeAccountSettings,
    setActiveSection
  }), [isAccountSettingsOpen, activeSection, toggleAccountSettings, openAccountSettings, closeAccountSettings, setActiveSection]);

  return (
    <AccountSettingsContext.Provider value={contextValue}>
      {children}
    </AccountSettingsContext.Provider>
  );
};
